import { useState } from "react";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Send } from "lucide-react";
import WhatsAppIcon from "../components/icons";
import PageHero from "../components/PageHero";
import Seo from "../components/Seo";
import { Reveal } from "../components/Reveal";
import { api, formatApiError } from "../lib/api";
import { useLang } from "../i18n";
import { useContent } from "../lib/useContent";

export default function Contact() {
  const { t, pick, lang } = useLang();
  const c = useContent();
  const [form, setForm] = useState({ name: "", phone: "", email: "", subject: "", message: "" });
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    const errs = {};
    if (form.name.trim().length < 2) errs.name = t("common.required");
    if (form.phone.trim().length < 8) errs.phone = t("common.required");
    if (form.subject.trim().length < 2) errs.subject = t("common.required");
    if (form.message.trim().length < 5) errs.message = t("common.required");
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setBusy(true);
    try {
      await api.post("/contact", { ...form, email: form.email || null });
      toast.success(t("common.formSuccess"));
      setForm({ name: "", phone: "", email: "", subject: "", message: "" });
    } catch (err) {
      toast.error(formatApiError(err) || t("common.formError"));
    } finally {
      setBusy(false);
    }
  };

  const field = "w-full rounded-xl border border-ngo-deep/20 bg-white px-4 py-3.5 text-sm text-ngo-ink placeholder:text-ngo-ink/40 focus:outline-none focus:ring-2 focus:ring-ngo-leaf/50 focus:border-ngo-leaf transition-[box-shadow,border-color] duration-200";
  const label = "block text-sm font-semibold text-ngo-ink mb-2";
  const err = "mt-1.5 text-xs text-red-600";

  return (
    <main>
      <Seo
        title="संपर्क करें — Anansha Welfare Society Contact"
        description="Anansha Welfare Society से संपर्क करें — मोबाइल +91 9452529739, ईमेल ananshawelfaresociety@gmail.com, पता: 25, रायपुर, तहसील पिंडरा, वाराणसी, उत्तर प्रदेश 221201।"
        path="/contact"
        breadcrumb={["संपर्क"]}
      />
      <PageHero
        overline={{ hi: "संपर्क", en: "Contact" }}
        title={{ hi: "हमसे संपर्क करें", en: "Contact Us" }}
        description={{ hi: "कोई प्रश्न, सुझाव या सहयोग की इच्छा — हमें संदेश भेजें, हम जल्द उत्तर देंगे।", en: "Any question, suggestion or wish to collaborate — send us a message, we will respond soon." }}
        testid="contact-page-heading"
      />

      <section className="py-20 bg-ngo-cream">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 grid lg:grid-cols-[1fr_1.2fr] gap-10 items-start">
          <Reveal>
            <div className="bg-ngo-forest text-ngo-cream rounded-3xl p-8 sm:p-10">
              <h2 className="font-heading font-bold text-2xl">{pick({ hi: "संपर्क जानकारी", en: "Contact Information" })}</h2>
              <div className="mt-8 space-y-6 text-sm">
                <div className="flex items-start gap-3.5">
                  <MapPin size={19} className="mt-0.5 shrink-0 text-ngo-gold" />
                  <div>
                    <p className="font-semibold text-ngo-gold mb-1">{pick({ hi: "कार्यालय पता", en: "Office Address" })}</p>
                    <p data-testid="contact-address" className="text-ngo-mint/85 leading-relaxed">{lang === "hi" ? c.address_hi : c.address_en}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3.5">
                  <Phone size={19} className="mt-0.5 shrink-0 text-ngo-gold" />
                  <div>
                    <p className="font-semibold text-ngo-gold mb-1">{pick({ hi: "मोबाइल", en: "Mobile" })}</p>
                    <a data-testid="contact-phone" href={`tel:${c.phone.replace(/\s/g, "")}`} className="text-ngo-mint/85 hover:text-ngo-gold transition-colors duration-200">{c.phone}</a>
                  </div>
                </div>
                <div className="flex items-start gap-3.5">
                  <Mail size={19} className="mt-0.5 shrink-0 text-ngo-gold" />
                  <div>
                    <p className="font-semibold text-ngo-gold mb-1">{pick({ hi: "ई-मेल", en: "Email" })}</p>
                    <a data-testid="contact-email" href={`mailto:${c.email}`} className="text-ngo-mint/85 hover:text-ngo-gold transition-colors duration-200 break-all">{c.email}</a>
                  </div>
                </div>
              </div>
              <div className="mt-9 flex flex-wrap gap-3">
                <a
                  data-testid="contact-map-link"
                  href="https://www.google.com/maps/search/?api=1&query=25,+Raipur,+Pindra,+Varanasi,+Uttar+Pradesh+221201"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-ngo-mint text-ngo-forest font-semibold text-sm hover:bg-ngo-gold hover:text-ngo-ink hover:-translate-y-0.5 transition-[background-color,color,transform] duration-300"
                >
                  <MapPin size={15} /> {pick({ hi: "Google Maps पर देखें", en: "View on Google Maps" })}
                </a>
              </div>
              <div className="mt-4 flex flex-wrap gap-3">
                <a data-testid="contact-whatsapp-btn" href={`https://wa.me/${c.social.whatsapp || "919452529739"}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-[#25D366] text-white font-semibold text-sm hover:-translate-y-0.5 transition-transform duration-300">
                  <WhatsAppIcon size={16} /> {t("common.whatsapp")}
                </a>
                <a data-testid="contact-call-btn" href={`tel:${c.phone.replace(/\s/g, "")}`} className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-ngo-gold text-ngo-ink font-semibold text-sm hover:-translate-y-0.5 transition-transform duration-300">
                  <Phone size={15} /> {t("common.callNow")}
                </a>
                <a data-testid="contact-email-btn" href={`mailto:${c.email}`} className="inline-flex items-center gap-2 px-5 py-3 rounded-full border border-ngo-mint/40 text-ngo-mint font-semibold text-sm hover:bg-ngo-mint/10 transition-colors duration-300">
                  <Mail size={15} /> {t("common.emailUs")}
                </a>
              </div>
            </div>
          </Reveal>

          <Reveal i={1}>
            <form data-testid="contact-form" onSubmit={submit} noValidate className="bg-white rounded-3xl border border-ngo-deep/10 shadow-[0_20px_60px_rgba(23,107,58,0.10)] p-7 sm:p-10">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-11 h-11 rounded-xl bg-ngo-mint flex items-center justify-center text-ngo-deep">
                  <Send size={20} />
                </div>
                <h2 className="font-heading font-bold text-2xl text-ngo-ink">{pick({ hi: "संदेश भेजें", en: "Send a Message" })}</h2>
              </div>
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label className={label} htmlFor="c-name">{t("form.name")} *</label>
                  <input id="c-name" data-testid="contact-name" className={field} value={form.name} onChange={set("name")} />
                  {errors.name && <p className={err}>{errors.name}</p>}
                </div>
                <div>
                  <label className={label} htmlFor="c-phone">{t("form.phone")} *</label>
                  <input id="c-phone" data-testid="contact-phone-input" type="tel" className={field} value={form.phone} onChange={set("phone")} />
                  {errors.phone && <p className={err}>{errors.phone}</p>}
                </div>
                <div>
                  <label className={label} htmlFor="c-email">{t("form.email")}</label>
                  <input id="c-email" data-testid="contact-email-input" type="email" className={field} value={form.email} onChange={set("email")} />
                </div>
                <div>
                  <label className={label} htmlFor="c-subject">{t("form.subject")} *</label>
                  <input id="c-subject" data-testid="contact-subject" className={field} value={form.subject} onChange={set("subject")} />
                  {errors.subject && <p className={err}>{errors.subject}</p>}
                </div>
                <div className="sm:col-span-2">
                  <label className={label} htmlFor="c-message">{t("form.message")} *</label>
                  <textarea id="c-message" data-testid="contact-message" rows={5} className={field} value={form.message} onChange={set("message")} />
                  {errors.message && <p className={err}>{errors.message}</p>}
                </div>
              </div>
              <button
                type="submit"
                data-testid="contact-submit"
                disabled={busy}
                className="mt-8 w-full sm:w-auto px-10 py-4 rounded-full bg-ngo-deep text-white font-bold hover:bg-ngo-forest hover:-translate-y-0.5 disabled:opacity-60 transition-[background-color,transform,opacity] duration-300"
              >
                {busy ? t("common.sending") : t("common.send")}
              </button>
            </form>
          </Reveal>
        </div>

        <Reveal className="mt-14">
          <div data-testid="contact-map" className="rounded-[2rem] overflow-hidden border border-ngo-deep/10 shadow-[0_20px_60px_rgba(23,107,58,0.12)] bg-white">
            <iframe
              title="अंशा वेलफेयर सोसाइटी कार्यालय स्थान"
              src="https://www.google.com/maps?q=Raipur,+Pindra,+Varanasi,+Uttar+Pradesh+221201&output=embed"
              className="w-full h-96 border-0"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        </Reveal>
      </section>
    </main>
  );
}
